import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Timer from '../interactive/Timer';
import QCMRating from '../qcm/QCMRating';
import ScoreGauge from './ScoreGauge';
import { calculateWeightedScore } from '../../utils/scoreCalculation';

const InfoPanel = ({ 
    elapsedTime, 
    maxTime = null, 
    attemptCount = 1, 
    difficulty = null, 
    degreeOfCertainty = null,
    hoveredCertainty = null,
    showCertainty = false,
    isTimerRunning = false,
    qcmId = null,
    // Props pour le score en mode exam style
    selectedAnswers = new Set(),
    correctAnswers = [],
    selectedCertainties = new Map(),
    isExamStyle = false
}) => {
    const { t } = useTranslation();

    // Force re-render when hoveredCertainty changes
    useEffect(() => {
        // This will trigger a re-render when hoveredCertainty changes
    }, [hoveredCertainty]);

    const getDifficultyColor = (diff) => {
        if (diff <= 3) return 'text-green-400';
        if (diff <= 6) return 'text-yellow-400';
        return 'text-red-400';
    };

    const getDifficultyIcon = (diff) => {
        if (diff <= 3) {
            return (
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
            );
        } else if (diff <= 6) {
            return (
                <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
            );
        } else {
            return (
                <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
            );
        }
    };

    const getCertaintyColor = (certainty) => {
        switch (certainty) {
            case 'low': return 'text-red-400';
            case 'medium': return 'text-yellow-400';
            case 'high': return 'text-green-400';
            default: return 'text-gray-400';
        }
    };

    const getCertaintyLabel = (certainty) => {
        switch (certainty) {
            case 'low': return 'Faible';
            case 'medium': return 'Moyen';
            case 'high': return 'Élevé';
            default: return 'Non sélectionné';
        }
    };

    // État local pour les données de sélection en mode exam style
    const [examSelectionData, setExamSelectionData] = useState({
        selectedAnswers: new Set(),
        selectedCertainties: new Map(),
        correctAnswers: []
    });

    // Écouter les événements de mise à jour de sélection pour le mode exam style
    useEffect(() => {
        if (!isExamStyle) return;

        const handleSelectionUpdate = (event) => {
            console.log('🔔 InfoPanel received selection update:', event.detail);
            const { selectedAnswers, selectedCertainties, correctAnswers } = event.detail;
            const newData = {
                selectedAnswers: new Set(selectedAnswers),
                selectedCertainties: new Map(Object.entries(selectedCertainties)),
                correctAnswers: correctAnswers
            };
            console.log('🔔 InfoPanel updating examSelectionData:', {
                selectedAnswers: [...newData.selectedAnswers],
                correctAnswers: newData.correctAnswers,
                selectedCertainties: Object.fromEntries(newData.selectedCertainties)
            });
            setExamSelectionData(newData);
        };

        window.addEventListener('examQCMSelectionUpdate', handleSelectionUpdate);
        return () => {
            window.removeEventListener('examQCMSelectionUpdate', handleSelectionUpdate);
        };
    }, [isExamStyle]);

    // Calcul du score pondéré pour le mode exam style
    const weightedScore = useMemo(() => {
        console.log('🎯 Score calculation debug:', {
            isExamStyle,
            selectedAnswersSize: selectedAnswers.size,
            correctAnswersLength: correctAnswers.length,
            selectedAnswers: [...selectedAnswers],
            correctAnswers: correctAnswers,
            selectedCertainties: Object.fromEntries(selectedCertainties),
            examSelectionData: {
                selectedAnswers: [...examSelectionData.selectedAnswers],
                correctAnswers: examSelectionData.correctAnswers
            },
            attemptCount,
            elapsedTime,
            maxTime
        });

        if (!isExamStyle) {
            console.log('❌ InfoPanel: not in exam style mode');
            return null;
        }

        // TOUJOURS utiliser les données d'événements en mode exam style
        const actualSelectedAnswers = examSelectionData.selectedAnswers || new Set();
        const actualCorrectAnswers = examSelectionData.correctAnswers || [];
        const actualSelectedCertainties = examSelectionData.selectedCertainties || new Map();

        // Debug pour voir pourquoi les données ne sont pas transmises
        console.log('🔍 InfoPanel data transmission debug:', {
            examSelectionDataHasData: examSelectionData.selectedAnswers.size > 0,
            examSelectionDataSize: examSelectionData.selectedAnswers.size,
            propsHasData: selectedAnswers.size > 0,
            propsSize: selectedAnswers.size,
            willUseExamData: examSelectionData.selectedAnswers.size > 0
        });

        console.log('🔍 InfoPanel data selection debug:', {
            examSelectionDataSize: examSelectionData.selectedAnswers.size,
            examSelectionDataCorrectLength: examSelectionData.correctAnswers.length,
            examSelectionDataCertaintiesSize: examSelectionData.selectedCertainties.size,
            selectedAnswersSize: selectedAnswers.size,
            correctAnswersLength: correctAnswers.length,
            selectedCertaintiesSize: selectedCertainties.size,
            actualSelectedAnswersSize: actualSelectedAnswers.size,
            actualCorrectAnswersLength: actualCorrectAnswers.length,
            actualSelectedCertaintiesSize: actualSelectedCertainties.size
        });

        console.log('🔍 InfoPanel data selection:', {
            examSelectionData: {
                selectedAnswers: [...examSelectionData.selectedAnswers],
                correctAnswers: examSelectionData.correctAnswers,
                selectedCertainties: Object.fromEntries(examSelectionData.selectedCertainties)
            },
            actualSelectedAnswers: [...actualSelectedAnswers],
            actualCorrectAnswers: actualCorrectAnswers,
            actualSelectedCertainties: Object.fromEntries(actualSelectedCertainties)
        });

        if (actualSelectedAnswers.size === 0 || actualCorrectAnswers.length === 0) {
            console.log('❌ InfoPanel: no selections or correct answers', {
                selectedAnswersSize: actualSelectedAnswers.size,
                correctAnswersLength: actualCorrectAnswers.length,
                actualSelectedAnswers: [...actualSelectedAnswers],
                actualCorrectAnswers: actualCorrectAnswers
            });
            return null;
        }

        // Récupérer les certainties pour les réponses sélectionnées
        const certainties = [...actualSelectedAnswers].map(answer => 
            actualSelectedCertainties.get(answer) || degreeOfCertainty || 'high'
        );

        const result = calculateWeightedScore(
            actualSelectedAnswers,
            actualCorrectAnswers,
            attemptCount,
            elapsedTime,
            maxTime || 0,
            certainties
        );

        console.log('🎯 Final score calculation result:', {
            result,
            actualSelectedAnswers: [...actualSelectedAnswers],
            actualCorrectAnswers,
            certainties,
            attemptCount,
            elapsedTime,
            maxTime: maxTime || 0
        });
        return result;
    }, [isExamStyle, selectedAnswers, correctAnswers, selectedCertainties, degreeOfCertainty, attemptCount, elapsedTime, maxTime, examSelectionData]);



    return (
        <div className="space-y-3">
            {/* Difficulty Section */}
            {difficulty && (
                <div className="bg-gray-700 rounded-lg px-1 py-1 text-center">
                    <div className="text-gray-400 text-xs font-bold uppercase">{t('difficulty') || 'Difficulté'}</div>
                    <div className="w-1/2 mx-auto bg-gray-600 rounded-full h-2 mt-1 mb-1">
                        <div 
                            className="h-2 rounded-full transition-all duration-300"
                            style={{
                                width: `${(difficulty / 10) * 100}%`,
                                background: difficulty <= 3 
                                    ? '#10b981' 
                                    : difficulty <= 6 
                                        ? 'linear-gradient(to right, #10b981, #f59e0b)' 
                                        : 'linear-gradient(to right, #10b981, #f59e0b, #ef4444)'
                            }}
                        />
                    </div>
                    <div className="text-white font-bold text-sm flex items-center justify-center gap-1">
                        {difficulty <= 3 ? (
                            // Plume pour facile (1-3)
                            <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5l3.74-6.76zM16 8L2 22M22 16L8 2"/>
                            </svg>
                        ) : difficulty <= 6 ? (
                            // Haltère simple pour moyen (4-6)
                            <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M6 4h12v2H6V4zm0 14h12v2H6v-2zm0-7h12v2H6v-2z"/>
                                <circle cx="8" cy="6" r="1" fill="currentColor"/>
                                <circle cx="16" cy="6" r="1" fill="currentColor"/>
                            </svg>
                        ) : (
                            // Barre tordue pour difficile (7-10)
                            <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
                                <path d="M7 4l10 4M7 20l10-4" stroke="currentColor" strokeWidth="2" fill="none"/>
                            </svg>
                        )}
                        {difficulty}/10
                    </div>
                </div>
            )}

            {/* Timer Section */}
            {maxTime > 0 && (
                <div className="bg-gray-700 rounded-lg px-1 py-1 text-center">
                    <Timer
                        maxTimeInSeconds={maxTime}
                        isRunning={isTimerRunning}
                        onComplete={() => {}}
                    />
                </div>
            )}

            {/* Attempt Section */}
            <div className="bg-gray-700 rounded-lg px-1 py-1 text-center">
                <div className="text-gray-400 text-xs font-bold uppercase">{t('attempt') || 'Tentative'}</div>
                <div className="text-white font-bold text-2xl">{attemptCount}</div>
            </div>

            {/* Degree of Certainty Section */}
            {showCertainty && (
                <div className="bg-gray-700 rounded-lg px-1 py-1 text-center">
                    <div className="text-gray-400 text-xs font-bold uppercase">{t('certainty') || 'CERTAINTY'}</div>
                    <div className={`font-bold text-sm ${getCertaintyColor(hoveredCertainty?.level || degreeOfCertainty)}`}>
                        {hoveredCertainty && hoveredCertainty.level ? getCertaintyLabel(hoveredCertainty.level) : getCertaintyLabel(degreeOfCertainty)}
                    </div>
                </div>
            )}

            {/* QCM Rating Section */}
            {qcmId && (
                <QCMRating 
                    qcmId={qcmId}
                    onRatingChange={(rating) => {
                        console.log('QCM rating changed:', rating);
                    }}
                />
            )}

            {/* Score Gauge Section - Mode Exam Style */}
            {isExamStyle && (
                <ScoreGauge 
                    score={weightedScore?.score || 0}
                    maxScore={weightedScore?.maxScore || 10}
                    showLabel={true}
                    size="normal"
                />
            )}
        </div>
    );
};

export default InfoPanel;